import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { LOCApplication, AuditLog } from '../data/mockData';
import { formatCurrency } from '../theme';
import StatusBadge from '../components/StatusBadge';
import api from '../services/api';

const Dashboard: React.FC = () => {
  const [locApps, setLocApps] = useState<LOCApplication[]>([]);
  const [officerCount, setOfficerCount] = useState<number>(0);
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statsFromApi, setStatsFromApi] = useState<{ totalLOCs: number; pendingApprovals: number; totalDisbursed: number; activeOfficers: number } | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      // Try the dashboard stats endpoint first
      try {
        const statsRes = await api.dashboard.getStats();
        const statsData = statsRes.data || statsRes;
        if (statsData && typeof statsData === 'object' && 'totalLOCs' in statsData) {
          setStatsFromApi(statsData);
          if (Array.isArray(statsData.recentActivity) && statsData.recentActivity.length > 0) {
            setLogs(statsData.recentActivity);
          }
        }
      } catch {
        // Stats endpoint not available, will compute from individual endpoints
      }

      try {
        const [locRes, usersRes, logsRes] = await Promise.allSettled([
          api.loc.getAll(),
          api.users.getAll(),
          api.auditLogs.getAll(),
        ]);
        if (locRes.status === 'fulfilled') {
          const data = locRes.value.data || locRes.value;
          if (Array.isArray(data)) setLocApps(data);
        }
        if (usersRes.status === 'fulfilled') {
          const data = usersRes.value.data || usersRes.value;
          if (Array.isArray(data)) {
            setOfficerCount(data.filter((o: any) => o.status === 'active').length);
          }
        }
        if (logsRes.status === 'fulfilled') {
          const data = logsRes.value.data || logsRes.value;
          if (Array.isArray(data)) setLogs(data);
        }
      } catch (e: any) {
        setError(e.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const totalLOCs = statsFromApi?.totalLOCs ?? locApps.length;
  const pendingApprovals = statsFromApi?.pendingApprovals ?? locApps.filter(l => l.status === 'submitted' || l.status === 'under_review').length;
  const totalDisbursed = statsFromApi?.totalDisbursed ?? locApps
    .filter(l => ['approved', 'payment_80_done', 'final_settlement', 'completed'].includes(l.status))
    .reduce((sum, l) => sum + (Number(l.estimatedAmount) || 0), 0);
  const activeOfficers = statsFromApi?.activeOfficers ?? officerCount;

  const recentLOCs = [...locApps].sort((a, b) =>
    new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime()
  ).slice(0, 5);

  const recentLogs = logs.slice(0, 6);

  const statusCounts: Record<string, number> = {};
  locApps.forEach(l => { statusCounts[l.status] = (statusCounts[l.status] || 0) + 1; });

  return (
    <>
      <div className="header">
        <div className="header-left">
          <div>
            <h2>Dashboard</h2>
            <p>AIS Officers Healthcare Admin — Overview</p>
          </div>
        </div>
        <div className="header-right">
          <div className="search-bar">
            <span>🔍</span>
            <input placeholder="Search LOC, officer, hospital..." />
          </div>
        </div>
      </div>

      <div className="content">
        {loading && <div style={{ padding: 40, textAlign: 'center', fontSize: 16, color: '#94A3B8' }}>Loading...</div>}
        {error && <div style={{ padding: '12px 20px', background: 'rgba(239,68,68,0.1)', color: '#EF4444', borderRadius: 8, margin: '0 0 16px', fontSize: 13, fontWeight: 600 }}>{error}</div>}
        {/* Stats */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-card-header">
              <div className="stat-card-icon blue">📋</div>
              <span className="stat-card-change up">+3 today</span>
            </div>
            <h3>{totalLOCs}</h3>
            <p>Total LOC Applications</p>
          </div>
          <div className="stat-card">
            <div className="stat-card-header">
              <div className="stat-card-icon gold">⏳</div>
              {pendingApprovals > 0 && <span className="stat-card-change down">{pendingApprovals} pending</span>}
            </div>
            <h3>{pendingApprovals}</h3>
            <p>Pending Approvals</p>
          </div>
          <div className="stat-card">
            <div className="stat-card-header">
              <div className="stat-card-icon green">₹</div>
              <span className="stat-card-change up">+₹4.5L</span>
            </div>
            <h3>{formatCurrency(totalDisbursed)}</h3>
            <p>Total Amount Disbursed</p>
          </div>
          <div className="stat-card">
            <div className="stat-card-header">
              <div className="stat-card-icon blue">👤</div>
            </div>
            <h3>{activeOfficers}</h3>
            <p>Active Officers</p>
          </div>
        </div>

        <div className="grid-2">
          {/* Recent Applications */}
          <div className="card">
            <div className="card-header">
              <h3>Recent Applications</h3>
              <Link to="/applications" className="action-btn">View All →</Link>
            </div>
            <div className="card-body">
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Reference</th>
                      <th>Officer</th>
                      <th>Amount</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentLOCs.map(loc => (
                      <tr key={loc.id}>
                        <td>
                          <div style={{ fontWeight: 600, fontSize: 12 }}>{loc.refNumber}</div>
                          <div style={{ fontSize: 11, color: '#94A3B8' }}>{loc.hospitalName}</div>
                        </td>
                        <td style={{ fontSize: 13 }}>{loc.officerName}</td>
                        <td className="amount">{formatCurrency(loc.estimatedAmount)}</td>
                        <td>
                          <StatusBadge status={loc.status} />
                          {loc.isEmergency && <span className="badge emergency" style={{ marginLeft: 4 }}>
                            <span className="badge-dot" />EMG
                          </span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Audit Log */}
          <div className="card">
            <div className="card-header">
              <h3>Recent Activity</h3>
              <Link to="/audit" className="action-btn">View All →</Link>
            </div>
            <div className="card-body padded">
              {recentLogs.length === 0 && (
                <div style={{ padding: 24, textAlign: 'center', color: '#94A3B8', fontSize: 13 }}>
                  No recent activity yet. Actions like approvals, submissions, and logins will appear here.
                </div>
              )}
              {recentLogs.map(log => (
                <div key={log.id} className="timeline-item">
                  <div className={`timeline-dot ${log.action?.includes('Approved') ? 'success' : log.action?.includes('Submitted') || log.action?.includes('Login') ? 'active' : ''}`} />
                  <div>
                    <div className="timeline-text">
                      <strong>{(log as any).actorName || log.actor}</strong> — {log.action}
                    </div>
                    <div className="timeline-time">
                      {log.target} · {new Date(log.timestamp).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Status Distribution */}
        <div className="card">
          <div className="card-header">
            <h3>LOC Status Distribution</h3>
          </div>
          <div className="card-body padded">
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {Object.entries(statusCounts).map(([status, count]) => (
                <div key={status} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 16px', background: '#F7F8FA', borderRadius: 8
                }}>
                  <StatusBadge status={status} />
                  <span style={{ fontWeight: 700, fontSize: 16 }}>{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
